/**
 * Policy Violation Service
 * 
 * Calculates policy violations for timesheets based on organisation settings.
 * This service uses the policy violation settings from the backend to determine
 * which checks to run and what thresholds to use.
 */

import type { Timesheet } from '@/types/timesheet';
import type { 
  OrganisationTimesheetSettings,
  PolicyCheckType,
  WorkingProfile
} from '@/types/timesheetSettings';
import type { PolicyViolation } from '@/data/mockPolicyViolations';

type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

const DAY_KEYS: DayKey[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];



/**
 * Calculate daily hours from a timesheet
 */
function calculateDailyHours(timesheet: Timesheet): Record<DayKey, number> {
  const dailyHours: Record<DayKey, number> = {
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    sunday: 0,
  };

  timesheet.entries.forEach(entry => {
    DAY_KEYS.forEach(day => {
      dailyHours[day] += entry.hours[day] || 0;
    });
  });

  return dailyHours;
}

/**
 * Calculate total weekly hours from a timesheet
 */
function calculateWeeklyHours(timesheet: Timesheet): number {
  const dailyHours = calculateDailyHours(timesheet);
  return Object.values(dailyHours).reduce((sum, hours) => sum + hours, 0);
}

/**
 * Get expected daily hours from working profile
 */
function getExpectedDailyHours(workingProfile: WorkingProfile, day: DayKey): number {
  return workingProfile.workingDays[day]?.expectedHours || 0;
}

/**
 * Check if a day is a working day
 */
function isWorkingDay(workingProfile: WorkingProfile, day: DayKey): boolean {
  return workingProfile.workingDays[day]?.isWorkingDay || false;
}

/**
 * Get the date string for a specific day in the timesheet week
 */
function getDayDate(weekEnding: Date, day: DayKey): string {
  const dayIndex = DAY_KEYS.indexOf(day);
  const weekEndingDate = new Date(weekEnding);
  // Week ending is typically Sunday, so we need to calculate back
  const dayOffset = 6 - dayIndex; // Sunday = 0 offset, Monday = 6 offset
  const date = new Date(weekEndingDate);
  date.setDate(date.getDate() - dayOffset);
  return date.toISOString().split('T')[0];
}

/**
 * Calculate policy violations for a single timesheet
 */
export function calculateViolationsForTimesheet(
  timesheet: Timesheet,
  settings: OrganisationTimesheetSettings
): PolicyViolation[] {
  const violations: PolicyViolation[] = [];
  const { policyViolations, workingProfile, policyLimits } = settings;
  const { checks, displayMode, showInfoAlerts, suppressDailyWhenWeeklyExceeded, consolidateDailyWarnings, monitoringFrequency } = policyViolations;
  const runWeeklyChecks = !monitoringFrequency || monitoringFrequency === 'week';
  const runDailyChecks = !monitoringFrequency || monitoringFrequency === 'day';
  const effectiveFreq = monitoringFrequency || 'week';

  const dailyHours = calculateDailyHours(timesheet);
  const weeklyHours = calculateWeeklyHours(timesheet);
  const expectedWeeklyHours = workingProfile.standardHoursPerWeek;
  const weekEndingStr = timesheet.weekEnding instanceof Date 
    ? timesheet.weekEnding.toISOString().split('T')[0]
    : String(timesheet.weekEnding).split('T')[0];

  let violationId = 0;
  const generateId = () => `pv-${timesheet.id}-${++violationId}`;

  // Track if we have weekly overage (for suppression logic)
  let hasWeeklyOverage = false;

  // Check weekly overage (only when monitoring by week)
  const weeklyOverageCheck = checks['weekly-overage'];
  if (weeklyOverageCheck?.enabled && runWeeklyChecks) {
    const threshold = weeklyOverageCheck.threshold || 5;
    const thresholdHours = expectedWeeklyHours + threshold;
    
    if (weeklyHours > thresholdHours) {
      hasWeeklyOverage = true;
      violations.push({
        id: generateId(),
        employeeId: timesheet.employeeId,
        employeeName: timesheet.employeeName,
        weekEnding: weekEndingStr,
        violationType: 'weekly-overage',
        severity: weeklyOverageCheck.severity,
        message: weeklyHours > thresholdHours + threshold
          ? 'Weekly hours significantly exceed threshold'
          : 'Weekly hours exceed threshold',
        details: {
          expected: expectedWeeklyHours,
          actual: weeklyHours,
          threshold: thresholdHours,
        },
      });
    }
  }

  // Check daily overage (only when monitoring by day)
  const dailyOverageCheck = checks['daily-overage'];
  if (dailyOverageCheck?.enabled && runDailyChecks) {
    // Skip daily checks if weekly exceeded and suppression is enabled
    if (!suppressDailyWhenWeeklyExceeded || !hasWeeklyOverage) {
      const dailyViolations: PolicyViolation[] = [];
      
      DAY_KEYS.forEach(day => {
        const actualHours = dailyHours[day];
        const expectedHours = getExpectedDailyHours(workingProfile, day);
        
        if (expectedHours > 0 && actualHours > 0) {
          const threshold = dailyOverageCheck.threshold || 1;
          const thresholdHours = expectedHours + threshold;
          
          if (actualHours > thresholdHours) {
            dailyViolations.push({
              id: generateId(),
              employeeId: timesheet.employeeId,
              employeeName: timesheet.employeeName,
              weekEnding: weekEndingStr,
              date: getDayDate(timesheet.weekEnding, day),
              violationType: 'daily-overage',
              severity: dailyOverageCheck.severity,
              message: 'Daily hours exceed threshold',
              details: {
                expected: expectedHours,
                actual: actualHours,
                threshold: thresholdHours,
              },
            });
          }
        }
      });

      // Consolidate daily warnings if enabled
      if (consolidateDailyWarnings && dailyViolations.length > 1) {
        const totalExpected = dailyViolations.reduce((sum, v) => sum + v.details.expected, 0);
        const totalActual = dailyViolations.reduce((sum, v) => sum + v.details.actual, 0);
        const avgExpected = totalExpected / dailyViolations.length;
        violations.push({
          id: generateId(),
          employeeId: timesheet.employeeId,
          employeeName: timesheet.employeeName,
          weekEnding: weekEndingStr,
          violationType: 'daily-overage',
          severity: dailyOverageCheck.severity,
          message: `Daily hours exceed threshold on ${dailyViolations.length} days`,
          details: {
            expected: avgExpected,
            actual: totalActual / dailyViolations.length,
            threshold: avgExpected + (dailyOverageCheck.threshold || 1),
          },
        });
      } else {
        violations.push(...dailyViolations);
      }
    }
  }

  // Check legal daily maximum
  const legalDailyCheck = checks['legal-daily-maximum'];
  if (legalDailyCheck?.enabled && policyLimits?.legalMaxHoursPerDay) {
    const limit = policyLimits.legalMaxHoursPerDay;
    
    DAY_KEYS.forEach(day => {
      const actualHours = dailyHours[day];
      const expectedHours = getExpectedDailyHours(workingProfile, day);
      
      if (actualHours > limit) {
        violations.push({
          id: generateId(),
          employeeId: timesheet.employeeId,
          employeeName: timesheet.employeeName,
          weekEnding: weekEndingStr,
          date: getDayDate(timesheet.weekEnding, day),
          violationType: 'legal-daily-maximum',
          severity: legalDailyCheck.severity,
          message: 'Daily hours exceed legal maximum',
          details: {
            expected: expectedHours,
            actual: actualHours,
            limit,
          },
        });
      }
    });
  }

  // Check legal weekly maximum
  const legalWeeklyCheck = checks['legal-weekly-maximum'];
  if (legalWeeklyCheck?.enabled && policyLimits?.legalMaxHoursPerWeek) {
    const limit = policyLimits.legalMaxHoursPerWeek;
    
    if (weeklyHours > limit) {
      violations.push({
        id: generateId(),
        employeeId: timesheet.employeeId,
        employeeName: timesheet.employeeName,
        weekEnding: weekEndingStr,
        violationType: 'legal-weekly-maximum',
        severity: legalWeeklyCheck.severity,
        message: 'Weekly hours exceed legal maximum',
        details: {
          expected: expectedWeeklyHours,
          actual: weeklyHours,
          limit,
        },
      });
    }
  }

  // Check missing hours (mode-specific)
  const missingHoursCheck = effectiveFreq === 'week'
    ? (checks['weekly-missing-hours'] || checks['missing-hours'])
    : (checks['daily-missing-hours'] || checks['missing-hours']);

  if (missingHoursCheck?.enabled) {
    if (effectiveFreq === 'week') {
      const threshold = missingHoursCheck.threshold || 8;
      const minimumHours = expectedWeeklyHours - threshold;

      if (weeklyHours < minimumHours) {
        violations.push({
          id: generateId(),
          employeeId: timesheet.employeeId,
          employeeName: timesheet.employeeName,
          weekEnding: weekEndingStr,
          violationType: 'missing-hours',
          severity: missingHoursCheck.severity,
          message: 'Weekly hours below expected',
          details: {
            expected: expectedWeeklyHours,
            actual: weeklyHours,
            threshold: minimumHours,
          },
        });
      }
    } else {
      const threshold = missingHoursCheck.threshold || 2;

      DAY_KEYS.forEach(day => {
        const actualHours = dailyHours[day];
        const expectedHours = getExpectedDailyHours(workingProfile, day);

        if (expectedHours > 0) {
          const minimumHours = expectedHours - threshold;

          if (actualHours < minimumHours) {
            violations.push({
              id: generateId(),
              employeeId: timesheet.employeeId,
              employeeName: timesheet.employeeName,
              weekEnding: weekEndingStr,
              date: getDayDate(timesheet.weekEnding, day),
              violationType: 'missing-hours',
              severity: missingHoursCheck.severity,
              message: 'Daily hours below expected',
              details: {
                expected: expectedHours,
                actual: actualHours,
                threshold: minimumHours,
              },
            });
          }
        }
      });
    }
  }

  // Check non-working day entries (mode-specific)
  const nonWorkingDayCheck = effectiveFreq === 'week'
    ? (checks['weekly-non-working-day'] || checks['non-working-day'])
    : (checks['daily-non-working-day'] || checks['non-working-day']);

  if (nonWorkingDayCheck?.enabled) {
    DAY_KEYS.forEach(day => {
      const actualHours = dailyHours[day];
      const isWorking = isWorkingDay(workingProfile, day);

      if (!isWorking && actualHours > 0) {
        violations.push({
          id: generateId(),
          employeeId: timesheet.employeeId,
          employeeName: timesheet.employeeName,
          weekEnding: weekEndingStr,
          date: getDayDate(timesheet.weekEnding, day),
          violationType: 'non-working-day',
          severity: nonWorkingDayCheck.severity,
          message: `Hours logged on non-working day (${day})`,
          details: {
            expected: 0,
            actual: actualHours,
            threshold: 0,
          },
        });
      }
    });
  }

  // Apply display mode filtering
  let filteredViolations = violations;
  
  if (displayMode === 'critical-only') {
    filteredViolations = violations.filter(v => v.severity === 'high');
  } else if (!showInfoAlerts) {
    filteredViolations = violations.filter(v => v.severity !== 'low');
  }

  return filteredViolations;
}

/**
 * Calculate violations for multiple timesheets
 */
export function calculateViolationsForTimesheets(
  timesheets: Timesheet[],
  settings: OrganisationTimesheetSettings
): Map<number, PolicyViolation[]> {
  const violationsMap = new Map<number, PolicyViolation[]>();
  
  timesheets.forEach(timesheet => {
    const violations = calculateViolationsForTimesheet(timesheet, settings);
    violationsMap.set(timesheet.id, violations);
  });
  
  return violationsMap;
}

/**
 * Get violation summary statistics
 */
export function getViolationSummary(violationsMap: Map<number, PolicyViolation[]>): {
  total: number;
  timesheetsWithViolations: number;
  bySeverity: {
    high: number;
    medium: number;
    low: number;
  };
  byType: Record<PolicyCheckType, number>;
} {
  let total = 0;
  let timesheetsWithViolations = 0;
  const bySeverity = { high: 0, medium: 0, low: 0 };
  const byType: Record<PolicyCheckType, number> = {
    'daily-overage': 0,
    'weekly-overage': 0,
    'legal-daily-maximum': 0,
    'legal-weekly-maximum': 0,
    'missing-hours': 0,
    'non-working-day': 0,
    'daily-missing-hours': 0,
    'weekly-missing-hours': 0,
    'daily-non-working-day': 0,
    'weekly-non-working-day': 0,
  };

  violationsMap.forEach(violations => {
    if (violations.length > 0) {
      timesheetsWithViolations++;
      total += violations.length;
      
      violations.forEach(v => {
        bySeverity[v.severity]++;
        byType[v.violationType]++;
      });
    }
  });

  return { total, timesheetsWithViolations, bySeverity, byType };
}

/**
 * Get the highest severity from a list of violations
 */
export function getHighestSeverity(violations: PolicyViolation[]): 'high' | 'medium' | 'low' | null {
  if (violations.length === 0) return null;
  
  if (violations.some(v => v.severity === 'high')) return 'high';
  if (violations.some(v => v.severity === 'medium')) return 'medium';
  return 'low';
}

/**
 * Filter violations by severity
 */
export function filterViolationsBySeverity(
  violations: PolicyViolation[],
  severity: 'high' | 'medium' | 'low'
): PolicyViolation[] {
  return violations.filter(v => v.severity === severity);
}
